import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

/**
 * Account settings.
 *
 * Only what is actually yours lives here — a name to sign your corrections
 * with, and the two things every account should be able to do without asking
 * anyone: leave, and take a copy of what you made.
 */
export function Settings() {
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [saved, setSaved] = useState<{ name: string; handle: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      const user = data.user;
      if (!user) { navigate("/signin"); return; }
      setEmail(user.email ?? null);
      setUserId(user.id);
      const { data: profile } = await supabase
        .from("profiles").select("display_name,handle").eq("id", user.id).maybeSingle();
      const name = profile?.display_name ?? "";
      const tag = profile?.handle ?? "";
      setDisplayName(name);
      setHandle(tag);
      setSaved({ name, handle: tag });
    });
  }, []);

  const dirty = saved !== null
    && (displayName !== saved.name || handle !== saved.handle);

  async function save() {
    if (!userId) return;
    setBusy(true); setProblem(null); setNote(null);
    try {
      const { error } = await supabase.from("profiles").upsert({
        id: userId,
        display_name: displayName.trim() || null,
        handle: handle.trim() || null,
      });
      if (error) throw error;
      setSaved({ name: displayName, handle });
      setNote("Saved.");
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  async function exportMine() {
    if (!userId) return;
    const [setlists, corrections, charts] = await Promise.all([
      supabase.from("setlists").select("*"),
      supabase.from("corrections_log").select("*").eq("author", userId),
      supabase.from("song_charts").select("*").eq("author", userId),
    ]);
    const blob = new Blob([JSON.stringify({
      exported: new Date().toISOString(),
      account: { id: userId, email },
      setlists: setlists.data ?? [],
      corrections: corrections.data ?? [],
      charts: charts.data ?? [],
    }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "everychord-my-data.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  if (!email) return <div className="page narrow"><p className="muted">Loading…</p></div>;

  return (
    <div className="page narrow">
      <h1 className="serif hero-small">Your account</h1>

      {note && <p className="notice">{note}</p>}
      {problem && <p className="notice notice-bad">{problem}</p>}

      <section className="settings-block">
        <h2 className="section-heading serif">Signed in as</h2>
        <p className="mono">{email}</p>
        <p className="muted small">
          Sign-in is by emailed link, so there is no password to change.
        </p>
      </section>

      <section className="settings-block">
        <h2 className="section-heading serif">How you are credited</h2>
        <p className="muted small">
          Corrections and chart edits are public and carry a name. Leave these
          empty and they simply say “someone”.
        </p>
        <label className="field">
          <span className="field-label">Display name</span>
          <input className="search" value={displayName} placeholder="Mihael"
                 onChange={(event) => setDisplayName(event.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Handle</span>
          <input className="search mono" value={handle} placeholder="mihael"
                 onChange={(event) => setHandle(event.target.value)} />
        </label>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
      </section>

      <section className="settings-block">
        <h2 className="section-heading serif">Your data</h2>
        <p className="muted small">
          Everything you have made here — setlists, corrections and chart edits —
          as one file.
        </p>
        <button className="btn" onClick={exportMine}>Download a copy</button>
      </section>

      <section className="settings-block">
        <h2 className="section-heading serif">Leaving</h2>
        <p className="muted small">
          Deleting the account removes your setlists and unlinks your name from
          corrections you made. The corrections themselves stay, because other
          people's charts now depend on them — they simply stop being attributed
          to you. Email <a href="mailto:hello@everychord.net">hello@everychord.net</a>{" "}
          and it will be done by hand; there is no automated button for something
          irreversible.
        </p>
        <button className="btn" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </section>

      <p className="muted small">
        <Link to="/">Back to songs</Link>
      </p>
    </div>
  );
}
